// src/components/modals/ChestOpeningModal.jsx
import React, { useState } from 'react';
import { Zap, Gift, Sparkles, X } from 'lucide-react';
import Mascot from '../Mascot';
import { playSound } from '../../utils/sounds';
import { getMascotDialogue } from '../../data/mascotDialogues';

export default function ChestOpeningModal({ 
  userProgress, 
  onPurchase, 
  onClose, 
  cost, 
  currentSkin,
  isLegendary = false,
  onInsufficientXp  // ✅ Nueva prop
}) {
  const [step, setStep] = useState('confirm'); 
  const [wonItem, setWonItem] = useState(null);

  const currentXp = userProgress?.xp || 0;

  // ✅ Función auxiliar para vibración (solo dispositivos compatibles)
  const triggerVibration = (pattern) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  };

  const handleStartOpening = () => {
    // ✅ VALIDAR XP INSUFICIENTE
    if (currentXp < cost) {
      playSound('wrong');
      
      // ✅ Llamar al handler de error de XP
      const missingXp = cost - currentXp;
      onInsufficientXp?.({
        cost: cost,
        current: currentXp,
        missing: missingXp,
        itemName: 'Cofre Sorpresa'
      });
      
      return;
    }

    // 🌟 INICIO ÉPICO
    playSound('wow');
    setStep('opening');
    
    // ✅ Vibración de "sacudida" (patrón de pulsos cortos)
    triggerVibration([50, 100, 50, 100, 50, 100, 50, 100, 50, 100]);

    setTimeout(() => {
      const result = onPurchase(cost); 
      if (result) {
        setWonItem(result);
        setStep('reward');
        playSound('wow');
        
        // ✅ Vibración de "impacto" final (pulso largo y fuerte)
        triggerVibration(400); 
      } else {
        onClose(); 
      }
    }, 2500); 
  };

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 overflow-hidden">
      {/* Fondo dinámico: Oscurece en la apertura */}
      <div 
        className={`absolute inset-0 backdrop-blur-md transition-colors duration-1000 ${
          step === 'opening' ? 'bg-black/95' : 'bg-slate-900/80'
        }`} 
        onClick={step !== 'opening' ? onClose : undefined} 
      />

      {/* BOTÓN DE CIERRE GLOBAL */}
      <button 
        onClick={onClose}
        className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-all z-[260] backdrop-blur-sm border border-white/10"
      >
        <X size={24} />
      </button>

      {/* --- PASO 1: CONFIRMACIÓN --- */}
      {step === 'confirm' && (
        <div className="bg-white rounded-[2.5rem] p-6 w-full max-w-xs sm:max-w-sm text-center shadow-2xl relative animate-in zoom-in-95 border-4 border-indigo-100">
          <div className="absolute -top-16 -right-10 w-28 h-28 sm:w-32 sm:h-32 drop-shadow-2xl">
            <Mascot type={currentSkin} message={getMascotDialogue('chest_open', currentSkin, isLegendary)} />
          </div>
          
          <div className="mt-10 sm:mt-12 space-y-4">
            <h3 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tighter uppercase italic">¿Abrir Cofre?</h3>
            <p className="text-slate-400 font-bold text-xs px-4 leading-relaxed">
              Dentro aguardan skins legendarias y secretos bilingües.
            </p>
            
            <div className="bg-indigo-50 p-4 sm:p-5 rounded-3xl border-2 border-indigo-100 shadow-inner">
              <p className="text-[10px] sm:text-xs font-black text-indigo-400 uppercase tracking-widest mb-1">
                Son solo {cost} XP
              </p>
              <div className="flex items-center justify-center gap-2 text-3xl sm:text-4xl font-black text-indigo-600">
                <Zap fill="currentColor" className="text-yellow-400" /> {cost}
              </div>
              
              {/* ✅ Mostrar XP actual si no alcanza */}
              {currentXp < cost && (
                <p className="text-xs font-bold text-rose-500 mt-2">
                  Tienes {currentXp} XP · Necesitas {cost} XP
                </p>
              )}
            </div>

            {/* ✅ BOTÓN SIEMPRE ACTIVO */}
            <button 
              onClick={handleStartOpening} 
              className={`w-full py-4 rounded-2xl font-black text-lg sm:text-xl shadow-xl border-b-4 active:border-b-0 active:translate-y-1 transition-all ${
                currentXp >= cost
                  ? 'bg-indigo-600 text-white border-indigo-800 hover:bg-indigo-500'
                  : 'bg-rose-500 text-white border-rose-700 hover:bg-rose-400'
              }`}
            >
              {currentXp >= cost ? '🎁 ¡SÍ, ABRIR!' : '⚠️ PROBAR SUERTE'}
            </button>
          </div>
        </div>
      )}

      {/* --- PASO 2: APERTURA ÉPICA --- */}
      {step === 'opening' && (
        <div className="relative flex flex-col items-center justify-center w-full h-full">
          <div className="absolute w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-[conic-gradient(from_0deg,transparent_0deg,rgba(255,255,255,0.4)_20deg,transparent_40deg)] animate-spin-slow rounded-full opacity-40" />
          <div className="absolute w-64 h-64 sm:w-96 sm:h-96 bg-indigo-500/40 blur-[80px] rounded-full animate-pulse" />
          
          <div className="relative z-10 animate-shake-intense">
            <div className="animate-bounce">
              <Gift size={100} className="text-yellow-200 sm:w-[140px] sm:h-[140px] drop-shadow-[0_0_30px_rgba(252,211,77,0.9)]" />
            </div>
          </div>

          <div className="relative z-20 mt-8 text-center px-6">
            <h2 className="text-4xl sm:text-6xl font-black text-white italic animate-pulse tracking-tighter uppercase drop-shadow-[0_0_20px_rgba(255,255,255,0.5)]">
              ¡Abriendo!
            </h2>
            <div className="flex gap-2 justify-center mt-4">
              <Sparkles className="text-yellow-400 animate-spin" size={24} />
              <Sparkles className="text-yellow-400 animate-ping" size={24} />
              <Sparkles className="text-yellow-400 animate-spin" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* --- PASO 3: RECOMPENSA --- */}
      {step === 'reward' && wonItem && (
        <div className="bg-white rounded-[3rem] p-8 w-full max-w-sm text-center shadow-[0_0_100px_rgba(79,70,229,0.4)] relative animate-in zoom-in-75 duration-500 border-8 border-amber-300">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-white rounded-[2.5rem] -z-10" />
          <Sparkles className="absolute -top-10 -right-10 text-amber-400 animate-bounce" size={80} />
          <Sparkles className="absolute -bottom-10 -left-10 text-amber-400 animate-pulse" size={60} />

          <h3 className="text-[10px] font-black text-amber-600 uppercase tracking-[0.3em] mb-2">¡Desbloqueo Legendario!</h3>
          <h2 className="text-3xl font-black text-slate-800 mb-6 leading-tight">¡LO LOGRASTE!</h2>
          
          <div className="bg-white rounded-[2rem] p-8 mb-8 shadow-inner border-2 border-amber-100 flex flex-col items-center">
            {wonItem.category === 'skin' && wonItem.img ? (
              <img 
                src={wonItem.img} 
                className="w-40 h-40 object-contain mb-4 animate-in slide-in-from-bottom-10 duration-700" 
                alt={wonItem.name}
              />
            ) : (
              <span className="text-6xl mb-4 animate-bounce block">
                {wonItem.emoji || '🎁'}
              </span>
            )}
            
            <p className="text-2xl font-black text-indigo-900 tracking-tight">{wonItem.name}</p>
            {wonItem.greeting && (
              <div className="mt-3 p-3 bg-indigo-50 rounded-2xl border border-indigo-100 text-xs italic font-bold text-indigo-700 leading-relaxed">
                "{wonItem.greeting}"
              </div>
            )}
          </div>

          <button 
            onClick={onClose} 
            className="w-full py-5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-2xl font-black text-xl shadow-xl shadow-orange-200 border-b-8 border-orange-700 active:border-b-0 active:translate-y-2 transition-all"
          >
            ¡GENIAL!
          </button>
        </div>
      )}
    </div>
  );
}
